(() => {
  if (window.VvSizeDrawer) return;

  const LENGTH_META = {
    R: { key: "regular", label: "Regular", hint: "5'6\" – 6'1\"", order: 0 },
    L: { key: "long", label: "Long", hint: "Over 6'1\"", order: 1 },
    S: { key: "short", label: "Short", hint: "Under 5'6\"", order: 2 },
  };

  const COLUMN_ORDER = ["R", "L", "S"];

  function rootPath() {
    return window.Shopify?.routes?.root || "/";
  }

  function money(cents, fallback) {
    if (fallback) return fallback;
    if (cents == null || Number.isNaN(Number(cents))) return "";
    try {
      if (window.Shopify?.formatMoney) {
        return window.Shopify.formatMoney(
          Number(cents),
          window.themeVariables?.settings?.moneyFormat ||
            window.Shopify.money_format ||
            "${{amount}}",
        );
      }
    } catch {
      /* ignore */
    }
    return `$${(Number(cents) / 100).toFixed(2)}`;
  }

  function parseSrl(size) {
    const m = String(size || "")
      .trim()
      .match(/^(\d+)\s*([SRL])$/i);
    if (!m) return null;
    return { chest: m[1], length: m[2].toUpperCase() };
  }

  function optionValue(variant, name) {
    const needle = String(name || "").toLowerCase();
    const opts = variant.options || variant.selectedOptions || [];
    if (Array.isArray(opts) && opts.length && typeof opts[0] === "object") {
      const hit = opts.find((o) => String(o.name || "").toLowerCase() === needle);
      return hit?.value ?? null;
    }
    // Prestige often stores options as positional strings with option names on product
    return null;
  }

  function sizeOf(variant, sizeOptionIndex) {
    if (variant.size != null) return String(variant.size);
    const fromNamed = optionValue(variant, "Size");
    if (fromNamed != null) return String(fromNamed);
    if (Array.isArray(variant.options) && sizeOptionIndex >= 0) {
      return String(variant.options[sizeOptionIndex] ?? "");
    }
    // title like "Black / 40R" or "40R"
    const parts = String(variant.title || "").split(" / ");
    return String(parts[parts.length - 1] || "").trim();
  }

  function colorOf(variant, colorOptionIndex) {
    if (variant.color != null) return String(variant.color);
    const fromNamed = optionValue(variant, "Color") || optionValue(variant, "Colour");
    if (fromNamed != null) return String(fromNamed);
    if (Array.isArray(variant.options) && colorOptionIndex >= 0) {
      return String(variant.options[colorOptionIndex] ?? "");
    }
    return null;
  }

  function icons() {
    return {
      back: `<svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M15 6 9 12l6 6"/></svg>`,
      close: `<svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M6 6l12 12M18 6 6 18"/></svg>`,
    };
  }

  class SizeDrawer {
    constructor() {
      this.product = null;
      this.selectedVariantId = null;
      this.selectedColor = null;
      this.showBack = false;
      this.onBack = null;
      this.onAdded = null;
      this.syncForm = true;
      this.closeAfterAdd = true;
      this._built = false;
    }

    ensureDom() {
      if (this._built) return;
      this._built = true;
      document.documentElement.classList.add("vv-sd-root");

      this.overlay = document.createElement("div");
      this.overlay.className = "vv-sd-overlay";
      this.overlay.addEventListener("click", () => this.close({ all: true }));

      this.panel = document.createElement("aside");
      this.panel.className = "vv-sd-panel";
      this.panel.setAttribute("role", "dialog");
      this.panel.setAttribute("aria-modal", "true");
      this.panel.setAttribute("aria-label", "Select your size");

      const ic = icons();
      this.panel.innerHTML = `
        <div class="vv-sd-panel__header">
          <div class="vv-sd-panel__nav">
            <button type="button" class="vv-sd-icon-btn" data-vv-sd-back aria-label="Back">${ic.back}</button>
            <button type="button" class="vv-sd-icon-btn" data-vv-sd-close aria-label="Close">${ic.close}</button>
          </div>
          <div class="vv-sd-panel__titles">
            <h2 class="vv-sd-panel__title">Select your size</h2>
            <button type="button" class="vv-sd-guide" data-vv-sd-guide>View size guide</button>
          </div>
        </div>
        <div class="vv-sd-panel__body" data-vv-sd-body></div>
        <p class="vv-sd-error" data-vv-sd-error hidden></p>
        <div class="vv-sd-panel__footer">
          <div class="vv-sd-footer__meta">
            <span class="vv-sd-footer__price" data-vv-sd-price></span>
            <span class="vv-sd-footer__ship">Delivery: 2 – 5 business days</span>
          </div>
          <button type="button" class="vv-sd-add" data-vv-sd-add disabled>Add to bag</button>
        </div>
      `;

      document.body.append(this.overlay, this.panel);

      this.bodyEl = this.panel.querySelector("[data-vv-sd-body]");
      this.priceEl = this.panel.querySelector("[data-vv-sd-price]");
      this.errorEl = this.panel.querySelector("[data-vv-sd-error]");
      this.addBtn = this.panel.querySelector("[data-vv-sd-add]");
      this.backBtn = this.panel.querySelector("[data-vv-sd-back]");
      this.closeBtn = this.panel.querySelector("[data-vv-sd-close]");
      this.guideBtn = this.panel.querySelector("[data-vv-sd-guide]");

      this.backBtn.addEventListener("click", () => {
        if (typeof this.onBack === "function") this.onBack();
        else this.close();
      });
      this.closeBtn.addEventListener("click", () => this.close({ all: true }));
      this.addBtn.addEventListener("click", () => this.addToBag());
      this.guideBtn.addEventListener("click", () => this.openSizeGuide());

      document.addEventListener("keydown", (e) => {
        if (e.key === "Escape" && this.panel.classList.contains("is-open")) {
          this.close({ all: true });
        }
      });

      if (window.matchMedia("(max-width: 699px)").matches) {
        this.panel.classList.add("vv-sd-panel--sheet");
      }
    }

    openSizeGuide() {
      let profile =
        this.product?.sizeGuideProfile ||
        document.querySelector("[data-vvellor-size-finder]")?.dataset?.vvellorSizeFinder ||
        document.querySelector("[data-vvellor-size-guide]")?.dataset?.vvellorSizeGuide ||
        "suit-jacket";
      const gender =
        this.product?.sizeGuideGender ||
        document.querySelector("[data-vvellor-size-finder]")?.dataset?.vvellorGender ||
        document.querySelector("[data-vvellor-size-guide]")?.dataset?.vvellorGender ||
        null;
      if (gender === "women" && (profile === "uniform" || profile === "blazer")) {
        profile = "uniform-women";
      }
      // Interactive fit finder (full multi-step) — not the static chart modal
      if (typeof window.VellorSizeGuideApp?.openSizeFinder === "function") {
        window.VellorSizeGuideApp.openSizeFinder({ profile, gender });
        return;
      }
      const params = new URLSearchParams({ profile });
      if (gender) params.set("gender", gender);
      window.location.href = `${window.VellorSizeGuide?.pageUrl || "/pages/size-guide"}?${params.toString()}`;
    }

    open(opts = {}) {
      this.ensureDom();
      this.product = opts.product;
      if (!this.product?.variants?.length) return;

      this.showBack = Boolean(opts.showBack);
      this.onBack = opts.onBack || null;
      this.onAdded = opts.onAdded || null;
      this.syncForm = opts.syncForm !== false;
      this.closeAfterAdd = opts.closeAfterAdd !== false;
      this.selectedVariantId = opts.selectedVariantId || null;
      this.selectedColor = opts.selectedColor || this.detectSelectedColor() || null;

      this.backBtn.hidden = !this.showBack;
      this.backBtn.style.visibility = this.showBack ? "visible" : "hidden";

      this.clearError();
      this.render();
      this.overlay.classList.add("is-open");
      this.panel.classList.add("is-open");
      document.documentElement.style.overflow = "hidden";
    }

    close({ all = false } = {}) {
      this.panel?.classList.remove("is-open");
      if (all || !this.showBack) {
        this.overlay?.classList.remove("is-open");
        if (!document.querySelector(".vv-mm-panel.is-open")) {
          document.documentElement.style.overflow = "";
        }
      }
      if (all) {
        document.dispatchEvent(new CustomEvent("vv:size-drawer:close-all"));
      }
    }

    detectSelectedColor() {
      const checked = document.querySelector(
        '.variant-picker__option input[type="radio"]:checked',
      );
      const fieldset = checked?.closest(".variant-picker__option");
      const legend = fieldset?.querySelector("legend")?.textContent || "";
      if (/color|colour/i.test(legend)) {
        return checked.value || checked.getAttribute("value");
      }
      // Prestige may show selected value in a span
      const colorField = [...document.querySelectorAll(".variant-picker__option")].find((fs) =>
        /color|colour/i.test(fs.querySelector("legend")?.textContent || ""),
      );
      const span = colorField?.querySelector(".variant-picker__option-info span");
      return span?.textContent?.trim() || null;
    }

    optionIndexes() {
      const names = (this.product.options || []).map((o) =>
        typeof o === "string" ? o : o.name,
      );
      return {
        size: names.findIndex((n) => /^size$/i.test(n)),
        color: names.findIndex((n) => /^colou?r$/i.test(n)),
      };
    }

    variantsForColor(color) {
      const { size: sizeIdx, color: colorIdx } = this.optionIndexes();
      return (this.product.variants || []).filter((v) => {
        if (colorIdx < 0 || !color) return true;
        return String(colorOf(v, colorIdx)) === String(color);
      }).map((v) => ({
        ...v,
        _size: sizeOf(v, sizeIdx),
        _color: colorOf(v, colorIdx),
      }));
    }

    colors() {
      const { color: colorIdx } = this.optionIndexes();
      if (colorIdx < 0) return [];
      const set = [];
      for (const v of this.product.variants || []) {
        const c = colorOf(v, colorIdx);
        if (c && !set.includes(c)) set.push(c);
      }
      return set;
    }

    hasSrlGrid(variants) {
      const parsed = variants.map((v) => parseSrl(v._size)).filter(Boolean);
      const lengths = new Set(parsed.map((p) => p.length));
      return parsed.length >= 3 && [...lengths].some((l) => l === "R");
    }

    render() {
      const colors = this.colors();
      if (colors.length && !this.selectedColor) {
        this.selectedColor = colors[0];
      }
      if (colors.length && this.selectedColor && !colors.includes(this.selectedColor)) {
        this.selectedColor = colors[0];
      }

      const variants = this.variantsForColor(this.selectedColor);
      this.bodyEl.innerHTML = "";

      if (colors.length > 1) {
        const wrap = document.createElement("div");
        wrap.className = "vv-sd-colors";
        wrap.setAttribute("role", "list");
        for (const color of colors) {
          const label = document.createElement("label");
          label.className = "vv-sd-color";
          if (color === this.selectedColor) label.classList.add("is-selected");
          const input = document.createElement("input");
          input.type = "radio";
          input.name = "vv-sd-color";
          input.value = color;
          input.checked = color === this.selectedColor;
          input.addEventListener("change", () => {
            this.selectedColor = color;
            this.selectedVariantId = null;
            this.render();
          });
          const text = document.createElement("span");
          text.textContent = color;
          label.append(input, text);
          wrap.append(label);
        }
        this.bodyEl.append(wrap);
      }

      if (this.hasSrlGrid(variants)) this.renderSrlGrid(variants);
      else this.renderFlatSizes(variants);

      this.updateFooter();
    }

    renderSrlGrid(variants) {
      const byKey = new Map();
      const chests = new Set();
      for (const v of variants) {
        const p = parseSrl(v._size);
        if (!p) continue;
        chests.add(p.chest);
        byKey.set(`${p.chest}:${p.length}`, v);
      }
      const chestList = [...chests].sort((a, b) => Number(a) - Number(b));

      const wrap = document.createElement("div");
      wrap.className = "vv-sd-grid-wrap";
      const grid = document.createElement("div");
      grid.className = "vv-sd-grid";
      grid.setAttribute("role", "group");
      grid.setAttribute("aria-label", "Size length grid");

      for (const len of COLUMN_ORDER) {
        const meta = LENGTH_META[len];
        const head = document.createElement("div");
        head.className = "vv-sd-col-head";
        head.innerHTML = `<span class="vv-sd-col-head__name">${meta.label}</span><span class="vv-sd-col-head__hint">${meta.hint}</span>`;
        grid.append(head);
      }

      for (const chest of chestList) {
        for (const len of COLUMN_ORDER) {
          const variant = byKey.get(`${chest}:${len}`);
          grid.append(this.sizeButton(variant, len === "R" ? chest : `${chest}${len}`));
        }
      }

      wrap.append(grid);
      this.bodyEl.append(wrap);
    }

    renderFlatSizes(variants) {
      const grid = document.createElement("div");
      grid.className = "vv-sd-grid vv-sd-grid--flat";
      const sorted = [...variants].sort((a, b) => {
        const na = Number(String(a._size).replace(/\D/g, ""));
        const nb = Number(String(b._size).replace(/\D/g, ""));
        if (Number.isFinite(na) && Number.isFinite(nb) && na !== nb) return na - nb;
        return String(a._size).localeCompare(String(b._size));
      });
      for (const v of sorted) {
        grid.append(this.sizeButton(v, v._size));
      }
      this.bodyEl.append(grid);
    }

    sizeButton(variant, label) {
      const el = document.createElement("label");
      el.className = "vv-sd-size";
      if (!variant) {
        el.classList.add("is-empty");
        el.innerHTML = `<span>&nbsp;</span>`;
        return el;
      }
      if (!variant.available) el.classList.add("is-unavailable");
      if (String(variant.id) === String(this.selectedVariantId)) {
        el.classList.add("is-selected");
      }

      const input = document.createElement("input");
      input.type = "radio";
      input.name = "vv-sd-size";
      input.value = String(variant.id);
      input.disabled = !variant.available;
      input.checked = String(variant.id) === String(this.selectedVariantId) && variant.available;
      input.addEventListener("change", () => {
        if (!input.checked) return;
        this.selectedVariantId = variant.id;
        this.render();
      });

      const text = document.createElement("span");
      // SuitSupply Regular column shows numeric only; Long/Short keep suffix
      const p = parseSrl(variant._size);
      text.textContent = p && p.length === "R" ? p.chest : label || variant._size;

      el.append(input, text);
      return el;
    }

    currentVariant() {
      return (this.product?.variants || []).find(
        (v) => String(v.id) === String(this.selectedVariantId),
      );
    }

    updateFooter() {
      const v = this.currentVariant();
      const basePrice =
        this.product?.priceMoney ||
        money(this.product?.price) ||
        money(this.product?.variants?.[0]?.price, this.product?.variants?.[0]?.priceMoney);
      this.priceEl.textContent = v
        ? money(v.price, v.priceMoney) || basePrice
        : basePrice;
      this.addBtn.disabled = !(v && v.available);
    }

    productForm() {
      return (
        document.querySelector("product-form form[action*='/cart/add']") ||
        document.querySelector('form[action*="/cart/add"]')
      );
    }

    syncVariantToForm(variantId) {
      if (!this.syncForm) return;
      const form = this.productForm();
      const idInput = form?.elements?.namedItem("id") || form?.querySelector('input[name="id"]');
      if (idInput) {
        idInput.value = String(variantId);
        idInput.dispatchEvent(new Event("change", { bubbles: true }));
      }

      // Try to check matching Prestige size radio for consistency
      const variant = this.currentVariant();
      if (!variant) return;
      const { size: sizeIdx } = this.optionIndexes();
      const size = sizeOf(variant, sizeIdx);
      document.querySelectorAll(".variant-picker__option").forEach((fs) => {
        if (!/size/i.test(fs.querySelector("legend")?.textContent || "")) return;
        fs.querySelectorAll('input[type="radio"]').forEach((input) => {
          if (String(input.value) === String(size)) {
            input.checked = true;
            input.dispatchEvent(new Event("change", { bubbles: true }));
          }
        });
      });

      const triggerVal = document.querySelector("[data-vv-sd-trigger-value]");
      if (triggerVal) triggerVal.textContent = size || "Selected";
    }

    quantity() {
      const form = this.productForm();
      const qty = form?.elements?.namedItem("quantity") || form?.querySelector('input[name="quantity"]');
      const n = Number(qty?.value || 1);
      return Number.isFinite(n) && n > 0 ? n : 1;
    }

    async addToBag() {
      const variant = this.currentVariant();
      if (!variant?.available) {
        this.showError("Select an available size.");
        return;
      }

      this.syncVariantToForm(variant.id);
      this.addBtn.setAttribute("aria-busy", "true");
      document.documentElement.dispatchEvent(
        new CustomEvent("theme:loading:start", { bubbles: true }),
      );

      try {
        const sections = [];
        document.documentElement.dispatchEvent(
          new CustomEvent("cart:prepare-bundled-sections", {
            bubbles: true,
            detail: { sections },
          }),
        );

        const response = await fetch(`${rootPath()}cart/add.js`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
          body: JSON.stringify({
            items: [{ id: Number(variant.id), quantity: this.quantity() }],
            sections: sections.join(","),
            sections_url: window.location.pathname,
          }),
        });

        const responseJson = await response.json();
        if (!response.ok) {
          throw new Error(responseJson.description || responseJson.message || "Could not add to bag.");
        }

        if (
          window.themeVariables?.settings?.cartType === "page" ||
          window.themeVariables?.settings?.pageType === "cart"
        ) {
          window.location.href = `${rootPath()}cart`;
          return;
        }

        const cartContent = await (await fetch(`${rootPath()}cart.js`)).json();
        cartContent.sections = responseJson.sections;

        const form = this.productForm();
        const detail = {
          items: responseJson.items || [responseJson],
          cart: cartContent,
        };
        form?.dispatchEvent(new CustomEvent("variant:add", { bubbles: true, detail }));
        document.documentElement.dispatchEvent(
          new CustomEvent("cart:change", {
            bubbles: true,
            detail: { baseEvent: "variant:add", cart: cartContent },
          }),
        );

        this.clearError();
        if (typeof this.onAdded === "function") {
          this.onAdded({ variant, response: responseJson });
        }
        if (this.closeAfterAdd) this.close({ all: true });
      } catch (error) {
        this.showError(error.message || "Could not add to bag.");
        document.dispatchEvent(new CustomEvent("cart:refresh"));
      } finally {
        this.addBtn.removeAttribute("aria-busy");
        document.documentElement.dispatchEvent(
          new CustomEvent("theme:loading:end", { bubbles: true }),
        );
      }
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.hidden = false;
      this.errorEl.textContent = message;
    }

    clearError() {
      if (!this.errorEl) return;
      this.errorEl.hidden = true;
      this.errorEl.textContent = "";
    }
  }

  const drawer = new SizeDrawer();

  function markNativeSizeOptions() {
    document.querySelectorAll(".variant-picker__option").forEach((fs) => {
      const legend = fs.querySelector("legend")?.textContent || "";
      if (/size/i.test(legend)) fs.setAttribute("data-vv-sd-size-option", "true");
    });
    document.documentElement.classList.add("vv-sd-active");
  }

  function installTrigger(product) {
    if (!product?.variants?.length) return;
    if (document.querySelector("[data-vv-sd-trigger]")) return;

    const picker = document.querySelector("variant-picker");
    if (!picker) return;

    const wrap = document.createElement("div");
    wrap.className = "vv-sd-trigger";
    wrap.setAttribute("data-vv-sd-trigger", "");
    wrap.innerHTML = `
      <button type="button" class="vv-sd-trigger__btn" data-vv-sd-open>
        <span class="vv-sd-trigger__label">Select size</span>
        <span class="vv-sd-trigger__value" data-vv-sd-trigger-value>Choose</span>
      </button>
    `;

    const sizeFs = [...picker.querySelectorAll(".variant-picker__option")].find((fs) =>
      /size/i.test(fs.querySelector("legend")?.textContent || ""),
    );
    if (sizeFs) sizeFs.before(wrap);
    else picker.append(wrap);

    wrap.querySelector("[data-vv-sd-open]")?.addEventListener("click", () => {
      drawer.open({ product, showBack: false });
    });
  }

  function interceptBuyButtons(product) {
    document.querySelectorAll('product-form form[action*="/cart/add"]').forEach((form) => {
      form.addEventListener(
        "submit",
        (e) => {
          const idInput = form.elements?.namedItem("id") || form.querySelector('input[name="id"]');
          const id = idInput?.value;
          const variant = (product.variants || []).find((v) => String(v.id) === String(id));
          // If user never opened drawer and product has multiple sizes, nudge into drawer
          const sizeCount = new Set(
            (product.variants || []).map((v) => sizeOf(v, 0)),
          ).size;
          const triggerVal = document.querySelector("[data-vv-sd-trigger-value]")?.textContent;
          if (sizeCount > 1 && (!triggerVal || triggerVal === "Choose") && !variant?.available) {
            e.preventDefault();
            e.stopPropagation();
            drawer.open({ product });
          }
        },
        true,
      );
    });
  }

  function boot() {
    const dataEl = document.querySelector("[data-vv-sd-product]");
    if (!dataEl) return;
    let product;
    try {
      product = JSON.parse(dataEl.textContent || "{}");
    } catch {
      return;
    }
    markNativeSizeOptions();
    installTrigger(product);
    interceptBuyButtons(product);

    document.addEventListener("vv:open-size-drawer", (event) => {
      drawer.open(event.detail || { product });
    });
  }

  window.VvSizeDrawer = drawer;
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
